import { IconArrowRedoUp } from 'central-icons/IconArrowRedoUp';
import { IconArrowUndoUp } from 'central-icons/IconArrowUndoUp';
import { IconTrashCanSimple } from 'central-icons/IconTrashCanSimple';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';
import { IconButton, useDisclosure } from '@revelio/ui';

import { useToast } from '../../hooks/useToast';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import {
  useOptionalTabContentConfig,
  useOptionalTabContentItems,
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { usePageManager } from '../PageManagerProvider/PageManagerProvider';
import { EditParametersButton } from '../Reports/Parameters/EditParametersButton';
import { ClearPageDialog } from './ClearPageDialog';
import { DownloadMenuButton } from './DownloadMenuButton';
import { EditModeButtons } from './EditModeButtons';
import styles from './PageActionBar.module.css';
import { SavedViewsActionButton } from './SavedViewsActionButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { UserPreferencesButton } from './UserPreferencesButton';
import { ViewModeButtons } from './ViewModeButtons';

export const PageActionBar = ({
  isReportPage = false,
  hideEditButton = false,
}: {
  isReportPage?: boolean;
  hideEditButton?: boolean;
}) => {
  const { editMode } = useEditMode();
  const {
    saveChanges,
    discardChanges,
    config,
    hasUnsavedChanges,
    undo,
    redo,
    canUndo,
    canRedo,
  } = usePageConfig();
  const { dispatch: tabContentDispatch } = useTabContentConfig();

  const { templateId, action } = useParams<{
    templateId?: string;
    action?: string;
  }>();
  const isClone = action === 'clone';
  const isNewPage = !templateId && !isClone;

  const { id: pageId } = config;

  const toast = useToast();
  const navigate = useNavigate();
  const { viewPage } = usePageManager();

  const [isSaving, setIsSaving] = useState(false);

  const {
    isOpen: isDiscardDialogOpen,
    onOpen: onDiscardDialogOpen,
    onClose: onDiscardDialogClose,
  } = useDisclosure();
  const {
    isOpen: isClearPageDialogOpen,
    onOpen: onClearPageDialogOpen,
    onClose: onClearPageDialogClose,
  } = useDisclosure();
  // parameters modal state moved into EditParametersButton

  const iconButtonShapeProps = { shape: 'squared' as const };
  const undoRedoIconSize = 14;
  const tabContentItems = useOptionalTabContentItems();
  const tabContentConfig = useOptionalTabContentConfig();
  const hasContent =
    (config?.tabs ?? []).some((t) => t.tabContent.items.length > 0) ||
    (tabContentItems?.length ?? 0) > 0;

  const handleSetViewMode = ({ isSaved = false }: { isSaved: boolean }) => {
    if (isClone && templateId && !isSaved) {
      viewPage(templateId, true);
      return;
    }

    if (!editMode || !pageId) return;
    // A save can resolve before the guard's provider re-renders clean, and a blocked hop is dropped silently.
    viewPage(pageId, isSaved);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isReportPage) {
        const validate = (): { isValid: boolean; message?: string } => {
          const parameterSets = (config?.tabs ?? []).map(
            (tab) => tab.tabContent?.reportParameters ?? []
          );
          if (tabContentConfig) {
            parameterSets.push(tabContentConfig.reportParameters ?? []);
          }
          for (const params of parameterSets) {
            if (params.length === 0) continue;
            const primaryCount = params.filter((p) => p.isPrimary).length;
            if (primaryCount !== 1) {
              return {
                isValid: false,
                message:
                  'Each tab with parameters must have exactly one primary parameter.',
              };
            }
          }
          return { isValid: true };
        };
        const { isValid, message } = validate();
        if (!isValid) {
          toast({
            position: 'top-right',
            title: 'Invalid parameter configuration',
            description: message ?? 'Please review report parameters.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setIsSaving(false);
          return;
        }
      }
      if (await saveChanges()) {
        handleSetViewMode({ isSaved: true });
        const title = (() => {
          if (isClone) return 'Copy created';
          if (isNewPage) return 'Page created';
          return 'Changes saved';
        })();

        mixpanel.track(BuildADashTrackingEvents.SAVE_PAGE, {
          page_id: pageId,
        });
        toast({
          position: 'top-right',
          title,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch {
      toast({
        position: 'top-right',
        title: 'Failed to save changes',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmClearPage = () => {
    tabContentDispatch({ type: 'SET_ITEMS', payload: [] });
    onClearPageDialogClose();
  };

  const handleConfirmDiscard = () => {
    discardChanges();
    onDiscardDialogClose();
    !isNewPage &&
      toast({
        position: 'top-right',
        title: 'Your unsaved changes have been discarded.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

    const skipGuard = true;
    if (isNewPage) {
      navigate('/', { state: { skipGuard } });
    } else if (isClone && templateId) {
      viewPage(templateId, skipGuard);
    } else {
      viewPage(pageId, skipGuard);
    }
  };

  // auto-open now handled inside EditParametersButton

  return (
    <>
      <div className={classNames(styles.actionBar, styles.compact)}>
        {!editMode && (
          <>
            <DownloadMenuButton />
            {!isReportPage && <UserPreferencesButton />}
            {!isReportPage && <SavedViewsActionButton />}
            {!hideEditButton && <ViewModeButtons isReportPage={isReportPage} />}
          </>
        )}

        {editMode && (
          <>
            {isReportPage && <EditParametersButton />}
            <IconButton
              aria-label="Clear page"
              icon={<IconTrashCanSimple size={undoRedoIconSize} />}
              size="md"
              variant="ghost"
              {...iconButtonShapeProps}
              onClick={onClearPageDialogOpen}
            />
            <IconButton
              aria-label="Undo"
              icon={<IconArrowUndoUp size={undoRedoIconSize} />}
              size="md"
              variant="ghost"
              {...iconButtonShapeProps}
              onClick={undo}
              disabled={!canUndo}
            />
            <IconButton
              aria-label="Redo"
              icon={<IconArrowRedoUp size={undoRedoIconSize} />}
              size="md"
              variant="ghost"
              {...iconButtonShapeProps}
              onClick={redo}
              disabled={!canRedo}
            />
            <EditModeButtons
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              hasContent={hasContent}
              onSetViewMode={() => handleSetViewMode({ isSaved: false })}
              onDiscard={onDiscardDialogOpen}
              onSave={handleSave}
              isNewPage={isNewPage}
              isClone={isClone}
            />
          </>
        )}
      </div>

      <UnsavedChangesDialog
        isOpen={isDiscardDialogOpen}
        onClose={onDiscardDialogClose}
        onConfirm={handleConfirmDiscard}
        isNewPage={isNewPage}
      />
      <ClearPageDialog
        isOpen={isClearPageDialogOpen}
        onClose={onClearPageDialogClose}
        onConfirm={handleConfirmClearPage}
      />
    </>
  );
};
